/**
 * ERP API Service
 * 
 * Serviço centralizado para comunicação com a API do ERP.
 * Agrupa todos os endpoints dos módulos do sistema.
 * 
 * Módulos:
 * - Pessoas (Clientes, Fornecedores, Usuários, Grupos)
 * - Produtos (Produtos, Grupos, Marcas, Unidades)
 * - Estoque (Movimentações, Notas, Trocas)
 * - Vendas (Vendas, PDV, NF-e, Caixa)
 * - Financeiro (Contas, Extrato, Conciliação)
 * - Configurações (Certificados)
 */

import api from './api'

// ============================================
// MÓDULO PESSOAS
// ============================================
export const customerService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/people/customers', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/people/customers/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/people/customers', data)
    return response.data
  },
  createQuick: async (data) => {
    const response = await api.post('/api/people/customers/quick', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/people/customers/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/people/customers/${id}`)
    return response.data
  },
  previewImport: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/people/customers/import/preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },
  importFromXml: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/people/customers/import/xml', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }
}

export const supplierService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/people/suppliers', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/people/suppliers/${id}`)
    return response.data
  },
  findByCnpj: async (cnpj) => {
    // Remove formatação do CNPJ
    const cleanCnpj = cnpj.replace(/\D/g, '')
    // Busca na lista de fornecedores usando o filtro de busca
    const response = await api.get('/api/people/suppliers', { 
      params: { search: cleanCnpj, limit: 1 } 
    })
    // Verifica se encontrou um fornecedor com o CNPJ exato
    if (response.data.success && response.data.data && response.data.data.length > 0) {
      const supplier = response.data.data.find(s => 
        s.cpf_cnpj && s.cpf_cnpj.replace(/\D/g, '') === cleanCnpj
      )
      if (supplier) {
        return { success: true, data: supplier }
      }
    }
    return { success: false, data: null }
  },
  create: async (data) => {
    const response = await api.post('/api/people/suppliers', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/people/suppliers/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/people/suppliers/${id}`)
    return response.data
  },
  previewImport: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/people/suppliers/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },
  executeImport: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/people/suppliers/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  }
}

export const accessGroupService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/people/access-groups', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/people/access-groups/${id}`, {
      params: { t: Date.now() }
    })
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/people/access-groups', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/people/access-groups/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/people/access-groups/${id}`)
    return response.data
  }
}

export const userService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/people/users', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/people/users/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/people/users', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/people/users/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/people/users/${id}`)
    return response.data
  },
  signupLink: async () => {
    const response = await api.get('/api/people/users/signup-link')
    return response.data
  }
}

// Serviço de vendedores — acessível a todos os usuários com acesso ao ERP
// Usa endpoint dedicado que não requer permissão PEOPLE_USERS
export const sellerService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/people/sellers', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/people/sellers/${id}`)
    return response.data
  }
}

// ============================================
// MÓDULO PRODUTOS
// ============================================
export const productGroupService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/products/groups', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/products/groups/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/products/groups', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/products/groups/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/products/groups/${id}`)
    return response.data
  }
}

export const brandService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/products/brands', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/products/brands/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/products/brands', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/products/brands/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/products/brands/${id}`)
    return response.data
  }
}

export const unitService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/products/units', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/products/units/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/products/units', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/products/units/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/products/units/${id}`)
    return response.data
  }
}

export const productService = {
  getAll: async (filters = {}) => {
    // Para ERP, nunca passar ecommerce=true, sempre retornar produtos individuais
    // Remover ecommerce dos filtros se estiver presente
    const { ecommerce, ...erpFilters } = filters
    const response = await api.get('/api/products', { params: erpFilters })
    return response.data
  },
  getNextSku: async () => {
    const response = await api.get('/api/products/next-sku')
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/products/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/admin/products', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/admin/products/${id}`, data)
    return response.data
  },
  updateReferences: async (id, references) => {
    const response = await api.put(`/api/admin/products/${id}/references`, { references })
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/admin/products/${id}`)
    return response.data
  },
  // Históricos
  getPriceHistory: async (id, params = {}) => {
    const response = await api.get(`/api/products/${id}/history/prices`, { params })
    return response.data
  },
  getSalesHistory: async (id, params = {}) => {
    const response = await api.get(`/api/products/${id}/history/sales`, { params })
    return response.data
  },
  getStockHistory: async (id, params = {}) => {
    const response = await api.get(`/api/products/${id}/history/stock`, { params })
    return response.data
  },
  getPurchaseHistory: async (id, params = {}) => {
    const response = await api.get(`/api/products/${id}/history/purchases`, { params })
    return response.data
  }
}

export const productGridService = {
  getAll: async () => {
    const response = await api.get('/api/products/grids')
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/products/grids/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/products/grids', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/products/grids/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/products/grids/${id}`)
    return response.data
  },
  addProduct: async (gridId, productId) => {
    const response = await api.post(`/api/products/grids/${gridId}/products/${productId}`)
    return response.data
  },
  removeProduct: async (gridId, productId) => {
    const response = await api.delete(`/api/products/grids/${gridId}/products/${productId}`)
    return response.data
  },
  findProductByVariants: async (gridId, variants) => {
    const response = await api.post(`/api/products/grids/${gridId}/find-product`, { variants })
    return response.data
  }
}

export const productVariantService = {
  getByProductId: async (productId) => {
    const response = await api.get(`/api/products/${productId}/variants`)
    return response.data
  },
  updateProductVariants: async (productId, variants) => {
    const response = await api.post(`/api/products/${productId}/variants`, { variants })
    return response.data
  }
}

export const variantTypeService = {
  getAll: async () => {
    const response = await api.get('/api/products/variant-types')
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/products/variant-types/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/products/variant-types', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/products/variant-types/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/products/variant-types/${id}`)
    return response.data
  }
}

// ============================================
// MÓDULO ESTOQUE
// ============================================
export const stockMovementService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/stock/movements', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/stock/movements/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/stock/movements', data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/stock/movements/${id}`)
    return response.data
  }
}

export const invoiceEntryService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/stock/invoice-entries', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/stock/invoice-entries/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/stock/invoice-entries', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/stock/invoice-entries/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/stock/invoice-entries/${id}`)
    return response.data
  },
  addItem: async (id, item) => {
    const response = await api.post(`/api/stock/invoice-entries/${id}/items`, item)
    return response.data
  },
  deleteAllItems: async (id) => {
    const response = await api.delete(`/api/stock/invoice-entries/${id}/items`)
    return response.data
  },
  process: async (id) => {
    const response = await api.post(`/api/stock/invoice-entries/${id}/process`)
    return response.data
  },
  cancel: async (id) => {
    const response = await api.post(`/api/stock/invoice-entries/${id}/cancel`)
    return response.data
  },
  // Métodos de Precificação
  processPricing: async (id) => {
    const response = await api.post(`/api/stock/invoice-entries/${id}/process-pricing`)
    return response.data
  },
  getPricingSuggestions: async (id) => {
    const response = await api.get(`/api/stock/invoice-entries/${id}/pricing-suggestions`)
    return response.data
  },
  confirmPricingSuggestion: async (suggestionId, price) => {
    const response = await api.post(`/api/stock/pricing-suggestions/${suggestionId}/confirm`, { price })
    return response.data
  },
  rejectPricingSuggestion: async (suggestionId, reason) => {
    const response = await api.post(`/api/stock/pricing-suggestions/${suggestionId}/reject`, { reason })
    return response.data
  },
  modifyPricingSuggestion: async (suggestionId, price) => {
    const response = await api.post(`/api/stock/pricing-suggestions/${suggestionId}/modify`, { price })
    return response.data
  },
  reprocessPricingSuggestion: async (suggestionId, customMessage) => {
    const response = await api.post(`/api/stock/pricing-suggestions/${suggestionId}/reprocess`, { customMessage })
    return response.data
  },
  reprocessAllPricing: async (invoiceEntryId, customMessage) => {
    const response = await api.post(`/api/stock/invoice-entries/${invoiceEntryId}/reprocess-all-pricing`, { customMessage })
    return response.data
  },
  uploadXML: async (formData) => {
    const response = await api.post('/api/stock/invoice-entries/upload-xml', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    })
    return response.data
  },
  extractDuplicates: async (id) => {
    const response = await api.get(`/api/stock/invoice-entries/${id}/duplicates`)
    return response.data
  },
  createAccountsPayable: async (id, data) => {
    const response = await api.post(`/api/stock/invoice-entries/${id}/accounts-payable`, data)
    return response.data
  }
}

// ============================================
// MÓDULO ENTRADA DE MERCADORIA (INCOMING)
// ============================================
export const incomingService = {
  list: async (filters = {}) => {
    const response = await api.get('/api/stock/incoming', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/stock/incoming/${id}`)
    return response.data
  },
  getFinancialOptions: async () => {
    const response = await api.get('/api/stock/incoming/financial-options')
    return response.data
  },
  createAccountsPayable: async (id, data) => {
    const response = await api.post(`/api/stock/incoming/${id}/accounts-payable`, data)
    return response.data
  },
  uploadXml: async (id, file) => {
    const formData = new FormData()
    formData.append('xml', file)
    const response = await api.post(`/api/stock/incoming/${id}/upload-xml`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },
  createFromXml: async (file) => {
    const formData = new FormData()
    formData.append('xml', file)
    const response = await api.post('/api/stock/invoice-entries/create-from-xml', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },
  verifyG1Password: async (email, password) => {
    const response = await api.post('/api/stock/incoming/verify-g1-password', { email, password })
    return response.data
  },
  createProductFromXmlItem: async (invoiceEntryId, itemId, email, password, productData) => {
    const response = await api.post(`/api/stock/incoming/${invoiceEntryId}/create-product`, {
      itemId,
      email,
      password,
      productData
    })
    return response.data
  },
  updateStatus: async (id, status, additionalData = {}) => {
    const response = await api.put(`/api/stock/incoming/${id}/status`, { 
      status,
      ...additionalData
    })
    return response.data
  },
  cancel: async (id, reason = null) => {
    const response = await api.post(`/api/stock/incoming/${id}/cancel`, reason ? { reason } : {})
    return response.data
  },
  getInspection: async (id) => {
    const response = await api.get(`/api/stock/incoming/${id}/inspection`)
    return response.data
  },
  updateInspection: async (id, items) => {
    const response = await api.put(`/api/stock/incoming/${id}/inspection`, { items })
    return response.data
  },
  verify: async (id) => {
    const response = await api.post(`/api/stock/incoming/${id}/verify`)
    return response.data
  },
  getPricing: async (id) => {
    const response = await api.get(`/api/stock/incoming/${id}/pricing`)
    return response.data
  },
  updatePricing: async (id, items) => {
    const response = await api.put(`/api/stock/incoming/${id}/pricing`, { items })
    return response.data
  },
  linkItem: async (id, itemType, itemId, productId) => {
    const response = await api.put(`/api/stock/incoming/${id}/link-item`, {
      itemType,
      itemId,
      productId
    })
    return response.data
  },
  unlinkItem: async (id, itemId) => {
    const response = await api.put(`/api/stock/incoming/${id}/unlink-item`, {
      itemId
    })
    return response.data
  },
  getItems: async (invoiceEntryId) => {
    const response = await api.get(`/api/stock/incoming/${invoiceEntryId}/items`)
    return response.data
  },
  updateAddressing: async (invoiceEntryId, data) => {
    const response = await api.put(`/api/stock/incoming/${invoiceEntryId}/addressing`, data)
    return response.data
  },
  convertItemQuantity: async (invoiceEntryId, itemId, data) => {
    const response = await api.put(`/api/stock/incoming/${invoiceEntryId}/items/${itemId}/convert-quantity`, data)
    return response.data
  },
  backToTriagem: async (invoiceEntryId, skipInspection = true) => {
    const response = await api.put(`/api/stock/incoming/${invoiceEntryId}/back-to-triagem`, { skipInspection })
    return response.data
  },
  getView: async (invoiceEntryId) => {
    const response = await api.get(`/api/stock/incoming/${invoiceEntryId}/view`)
    return response.data
  }
}

// ============================================
// MÓDULO AJUSTE DE ESTOQUE FÍSICO/DIGITAL
// ============================================
export const stockAdjustmentService = {
  list: async (filters = {}) => {
    const response = await api.get('/api/stock/adjustments', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/stock/adjustments/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/stock/adjustments', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/stock/adjustments/${id}`, data)
    return response.data
  },
  approve: async (id) => {
    const response = await api.post(`/api/stock/adjustments/${id}/approve`)
    return response.data
  },
  reject: async (id, notes = null) => {
    const response = await api.post(`/api/stock/adjustments/${id}/reject`, { notes })
    return response.data
  },
  cancel: async (id) => {
    const response = await api.delete(`/api/stock/adjustments/${id}`)
    return response.data
  }
}

export const productCreationRequestService = {
  list: async (filters = {}) => {
    const response = await api.get('/api/products/creation-requests', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/products/creation-requests/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/products/creation-requests', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/products/creation-requests/${id}`, data)
    return response.data
  },
  approve: async (id) => {
    const response = await api.post(`/api/products/creation-requests/${id}/approve`)
    return response.data
  },
  reject: async (id, notes = null) => {
    const response = await api.post(`/api/products/creation-requests/${id}/reject`, { notes })
    return response.data
  },
  cancel: async (id) => {
    const response = await api.delete(`/api/products/creation-requests/${id}`)
    return response.data
  }
}

export const stockMovementTypeService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/stock/movement-types', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/stock/movement-types/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/stock/movement-types', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/stock/movement-types/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/stock/movement-types/${id}`)
    return response.data
  }
}

export const returnService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/stock/exchanges-returns', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/stock/exchanges-returns/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/stock/exchanges-returns', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/stock/exchanges-returns/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/stock/exchanges-returns/${id}`)
    return response.data
  },
  addItem: async (id, item) => {
    const response = await api.post(`/api/stock/exchanges-returns/${id}/items`, item)
    return response.data
  },
  approve: async (id) => {
    const response = await api.post(`/api/stock/exchanges-returns/${id}/approve`)
    return response.data
  },
  reject: async (id, reason) => {
    const response = await api.post(`/api/stock/exchanges-returns/${id}/reject`, { reason })
    return response.data
  },
  searchInvoices: async (searchTerm) => {
    const response = await api.get('/api/stock/exchanges-returns/search-invoices', {
      params: { search: searchTerm }
    })
    return response.data
  },
  searchSales: async (searchTerm) => {
    const response = await api.get('/api/stock/exchanges-returns/search-sales', {
      params: { search: searchTerm }
    })
    return response.data
  },
  getInvoiceItems: async (type, id) => {
    const response = await api.get(`/api/stock/exchanges-returns/invoice-items/${type}/${id}`)
    return response.data
  },
  getStockMovements: async (id) => {
    // Buscar movimentações relacionadas à devolução
    const response = await api.get('/api/stock/movements', {
      params: {
        referenceType: 'EXCHANGE_RETURN',
        referenceId: id
      }
    })
    return response.data
  }
}

export const customerCreditService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/stock/customer-credits', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/stock/customer-credits/${id}`)
    return response.data
  },
  getByCustomer: async (customerId, filters = {}) => {
    const response = await api.get(`/api/stock/customer-credits/customer/${customerId}`, { params: filters })
    return response.data
  },
  getAvailableBalance: async (customerId) => {
    const response = await api.get(`/api/stock/customer-credits/customer/${customerId}/balance`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/stock/customer-credits', data)
    return response.data
  },
  createManual: async (data) => {
    const response = await api.post('/api/stock/customer-credits/manual', data)
    return response.data
  },
  createManualDebit: async (data) => {
    const response = await api.post('/api/stock/customer-credits/manual-debit', data)
    return response.data
  },
  getLedger: async (customerId, params = {}) => {
    const response = await api.get(`/api/stock/customer-credits/customer/${customerId}/ledger`, { params })
    return response.data
  },
  approve: async (id) => {
    const response = await api.patch(`/api/stock/customer-credits/${id}/approve`)
    return response.data
  },
  use: async (id, amount) => {
    const response = await api.patch(`/api/stock/customer-credits/${id}/use`, { amount })
    return response.data
  },
  cancel: async (id) => {
    const response = await api.delete(`/api/stock/customer-credits/${id}`)
    return response.data
  }
}

export const locationService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/stock/locations', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/stock/locations/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/stock/locations', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/stock/locations/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/stock/locations/${id}`)
    return response.data
  }
}

// ============================================
// MÓDULO ESTOQUE - AUDITORIA
// ============================================
export const stockAuditService = {
  getList: async (filters = {}) => {
    const response = await api.get('/api/stock/audits', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/stock/audits/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/stock/audits', data)
    return response.data
  },
  start: async (id) => {
    const response = await api.post(`/api/stock/audits/${id}/start`)
    return response.data
  },
  syncProducts: async (id) => {
    const response = await api.post(`/api/stock/audits/${id}/sync-products`)
    return response.data
  },
  recordCount: async (auditId, itemId, data) => {
    const response = await api.post(`/api/stock/audits/${auditId}/items/${itemId}/audit`, data)
    return response.data
  },
  complete: async (id, options = {}) => {
    const response = await api.post(`/api/stock/audits/${id}/complete`, options)
    return response.data
  },
  zeroUnaudited: async (id) => {
    const response = await api.post(`/api/stock/audits/${id}/zero-unaudited`)
    return response.data
  },
  apply: async (id) => {
    const response = await api.post(`/api/stock/audits/${id}/apply`)
    return response.data
  },
  reopen: async (id) => {
    const response = await api.post(`/api/stock/audits/${id}/reopen`)
    return response.data
  },
  cancel: async (id, data) => {
    const response = await api.post(`/api/stock/audits/${id}/cancel`, data)
    return response.data
  },
  updateResponsible: async (id, responsibleUserId) => {
    const response = await api.put(`/api/stock/audits/${id}/responsible`, {
      responsibleUserId: responsibleUserId || null
    })
    return response.data
  },
  getEligibleResponsibles: async () => {
    const response = await api.get('/api/stock/audits/eligible-responsibles')
    return response.data
  }
}

// ============================================
// MÓDULO VENDAS
// ============================================
export const saleService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/sales/sales', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/sales/sales/${id}`)
    return response.data
  },
  verificarExtornosPendentes: async (id) => {
    const response = await api.get(`/api/sales/sales/${id}/extornos-pendentes`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/sales/sales', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/sales/sales/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/sales/sales/${id}`)
    return response.data
  },
  confirm: async (id) => {
    const response = await api.post(`/api/sales/sales/${id}/confirm`)
    return response.data
  },
  cancel: async (id) => {
    const response = await api.post(`/api/sales/sales/${id}/cancel`)
    return response.data
  },
  reopenPresale: async (id) => {
    const response = await api.post(`/api/sales/sales/${id}/reopen-presale`)
    return response.data
  },
  addItem: async (saleId, data) => {
    const response = await api.post(`/api/sales/sales/${saleId}/items`, data)
    return response.data
  },
  updateItem: async (saleId, itemId, data) => {
    const response = await api.put(`/api/sales/sales/${saleId}/items/${itemId}`, data)
    return response.data
  },
  removeItem: async (saleId, itemId) => {
    const response = await api.delete(`/api/sales/sales/${saleId}/items/${itemId}`)
    return response.data
  },
  addPayment: async (saleId, data) => {
    const response = await api.post(`/api/sales/sales/${saleId}/payments`, data)
    return response.data
  },
  finalizeSale: async (saleId, data) => {
    const response = await api.post(`/api/sales/sales/${saleId}/finalize`, data, {
      timeout: 120000
    })
    return response.data
  },
  setFiscalDocumentType: async (saleId, type) => {
    const response = await api.post(`/api/sales/sales/${saleId}/fiscal-document-type`, { type })
    return response.data
  },
  emitirDocumentoFiscal: async (saleId, data) => {
    const response = await api.post(`/api/sales/sales/${saleId}/emitir-documento-fiscal`, data)
    return response.data
  },
  finalizePresale: async (saleId) => {
    const response = await api.post(`/api/sales/sales/${saleId}/finalize-presale`)
    return response.data
  },
  finalizeQuote: async (saleId) => {
    const response = await api.post(`/api/sales/sales/${saleId}/finalize-quote`)
    return response.data
  },
  createFromQuote: async (quoteId, { type = 'presale' } = {}) => {
    const response = await api.post(`/api/sales/sales/from-quote/${quoteId}`, { type })
    return response.data
  },
  createFromOnlineOrder: async (orderId, { type = 'presale' } = {}) => {
    const response = await api.post(`/api/sales/sales/from-online-order/${orderId}`, { type })
    return response.data
  }
}

export const saleReturnService = {
  getPreview: async (saleId) => {
    const response = await api.get(`/api/sales/sales/${saleId}/return-nfe/preview`)
    return response.data
  },
  getStatus: async (saleId) => {
    const response = await api.get(`/api/sales/sales/${saleId}/return-nfe`)
    return response.data
  },
  emitir: async (saleId, data) => {
    const response = await api.post(`/api/sales/sales/${saleId}/return-nfe`, data, {
      timeout: 120000
    })
    return response.data
  },
  retryFiscal: async (saleId) => {
    const response = await api.post(`/api/sales/sales/${saleId}/return-nfe/retry-fiscal`, {}, {
      timeout: 120000
    })
    return response.data
  }
}

export const salePaymentService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/sales/sale-payments', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/sales/sale-payments/${id}`)
    return response.data
  },
  findByAuthorizationNumber: async (authorizationNumber) => {
    const response = await api.get(`/api/sales/sale-payments/by-authorization/${encodeURIComponent(authorizationNumber)}`)
    return response.data
  },
  getAuthorizationHistory: async (paymentId) => {
    const response = await api.get(`/api/sales/sale-payments/${paymentId}/authorization-history`)
    return response.data
  },
  getHistoryByAuthorizationNumber: async (authorizationNumber) => {
    const response = await api.get(`/api/sales/sale-payments/authorization-history/${encodeURIComponent(authorizationNumber)}`)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/sales/sale-payments/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/sales/sale-payments/${id}`)
    return response.data
  },
  markAsPaid: async (id, paidAmount) => {
    const response = await api.post(`/api/sales/sale-payments/${id}/mark-paid`, { paidAmount })
    return response.data
  }
}

export const paymentMethodService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/sales/payment-methods', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/sales/payment-methods/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/sales/payment-methods', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/sales/payment-methods/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/sales/payment-methods/${id}`)
    return response.data
  }
}

export const paymentTermService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/sales/payment-terms', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/sales/payment-terms/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/sales/payment-terms', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/sales/payment-terms/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/sales/payment-terms/${id}`)
    return response.data
  },
  calculateInstallments: async (id, amount) => {
    const response = await api.post(`/api/sales/payment-terms/${id}/calculate-installments`, { amount })
    return response.data
  }
}

export const invoiceService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/sales/invoices', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/sales/invoices/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/sales/invoices', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/sales/invoices/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/sales/invoices/${id}`)
    return response.data
  },
  emit: async (id) => {
    const response = await api.post(`/api/sales/invoices/${id}/emit`)
    return response.data
  },
  cancel: async (id) => {
    const response = await api.post(`/api/sales/invoices/${id}/cancel`)
    return response.data
  }
}

export const cashRegisterService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/sales/cash-registers', { params: filters })
    return response.data
  },
  getCurrent: async () => {
    const response = await api.get('/api/sales/cash-registers/current')
    return response.data
  },
  checkOpenStatus: async () => {
    const response = await api.get('/api/sales/cash-registers/check-status')
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/sales/cash-registers/${id}`)
    return response.data
  },
  open: async (openingAmount = 0, observation = null) => {
    const response = await api.post('/api/sales/cash-registers/open', { 
      openingAmount,
      observation 
    })
    return response.data
  },
  close: async (id, actualAmount, paymentAmounts = []) => {
    const response = await api.post(`/api/sales/cash-registers/${id}/close`, { 
      actualAmount,
      paymentAmounts 
    })
    return response.data
  },
  getPaymentMethods: async (id) => {
    const response = await api.get(`/api/sales/cash-registers/${id}/payment-methods`)
    return response.data
  },
  addMovement: async (id, data) => {
    const response = await api.post(`/api/sales/cash-registers/${id}/movements`, data)
    return response.data
  },
  // Métodos de Conferência de Caixa
  getReconciliationList: async (filters = {}) => {
    const response = await api.get('/api/sales/cash-registers/reconciliation', { params: filters })
    return response.data
  },
  getReconciliation: async (id) => {
    const response = await api.get(`/api/sales/cash-registers/${id}/reconciliation`)
    return response.data
  },
  saveReconciliation: async (id, reconciliations, finalize = false) => {
    const response = await api.post(`/api/sales/cash-registers/${id}/reconciliation`, {
      reconciliations,
      finalize
    })
    return response.data
  },
  updateReconciliation: async (id, reconciliations, finalize = false) => {
    const response = await api.put(`/api/sales/cash-registers/${id}/reconciliation`, {
      reconciliations,
      finalize
    })
    return response.data
  },
  finalizeReconciliation: async (id) => {
    const response = await api.post(`/api/sales/cash-registers/${id}/reconciliation/finalize`)
    return response.data
  }
}

// ============================================
// MÓDULO FINANCEIRO
// ============================================
function buildAccountReceivableQueryParams(filters = {}) {
  const params = { ...filters }

  if (Array.isArray(filters.status)) {
    params.status = filters.status
  }

  const rawCustomerIds = Array.isArray(filters.customerIds)
    ? filters.customerIds
    : (filters.customerIds != null && filters.customerIds !== ''
      ? String(filters.customerIds).split(',')
      : [])
  const customerIds = rawCustomerIds
    .map((id) => String(id).trim())
    .filter(Boolean)

  delete params.customerIds
  delete params.customerId
  delete params.selectedCustomerNames

  if (customerIds.length > 0) {
    // String CSV evita ambiguidade de array na query string
    params.customerIds = customerIds.join(',')
    if (customerIds.length === 1) {
      params.customerId = customerIds[0]
    }
  } else if (filters.customerId) {
    params.customerId = String(filters.customerId)
  }

  const hasDueDateFilter = Boolean(params.dueDateFrom || params.dueDateTo)
  if (hasDueDateFilter) {
    delete params.nextDays
    delete params.lastDays
  }

  Object.keys(params).forEach((key) => {
    const value = params[key]
    if (
      value === '' ||
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete params[key]
    }
  })

  return params
}

export const accountReceivableService = {
  getAll: async (filters = {}) => {
    const params = buildAccountReceivableQueryParams(filters)
    const response = await api.get('/api/financial/accounts-receivable', {
      params,
      paramsSerializer: {
        indexes: null
      }
    })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/financial/accounts-receivable/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/financial/accounts-receivable', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/financial/accounts-receivable/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/financial/accounts-receivable/${id}`)
    return response.data
  },
  markAsPaid: async (id, paidAmount, paymentDate, financialAccountId) => {
    const response = await api.post(`/api/financial/accounts-receivable/${id}/mark-paid`, {
      paidAmount,
      paymentDate,
      financialAccountId: financialAccountId || null
    })
    return response.data
  },
  calculateInterestAndFine: async (id) => {
    const response = await api.get(`/api/financial/accounts-receivable/${id}/calculate-interest`)
    return response.data
  },
  previewImport: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/financial/accounts-receivable/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },
  executeImport: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/financial/accounts-receivable/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  }
}

export const accountPayableService = {
  getAll: async (filters = {}) => {
    // Processar filtros para garantir que arrays sejam enviados corretamente
    const params = { ...filters }
    if (Array.isArray(filters.status)) {
      // Axios suporta arrays na query string, mas vamos garantir que seja enviado corretamente
      params.status = filters.status
    }
    const response = await api.get('/api/financial/accounts-payable', { 
      params,
      paramsSerializer: {
        indexes: null // Permite arrays na query string como status[]=pending&status[]=overdue
      }
    })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/financial/accounts-payable/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/financial/accounts-payable', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/financial/accounts-payable/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/financial/accounts-payable/${id}`)
    return response.data
  },
  markAsPaid: async (id, paidAmount, paymentDate, financialAccountId) => {
    const response = await api.post(`/api/financial/accounts-payable/${id}/mark-paid`, {
      paidAmount,
      paymentDate,
      financialAccountId: financialAccountId || null
    })
    return response.data
  },
  calculateInterestAndFine: async (id) => {
    const response = await api.get(`/api/financial/accounts-payable/${id}/calculate-interest`)
    return response.data
  },
  previewImport: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/financial/accounts-payable/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },
  executeImport: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/financial/accounts-payable/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  }
}

export const cashFlowService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/financial/cash-flow', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/financial/cash-flow/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/financial/cash-flow', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/financial/cash-flow/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/financial/cash-flow/${id}`)
    return response.data
  },
  reorder: async (orderedIds) => {
    const response = await api.post('/api/financial/cash-flow/reorder', { orderedIds })
    return response.data
  },
  getReport: async (dateFrom, dateTo) => {
    const response = await api.get('/api/financial/cash-flow/report', {
      params: { dateFrom, dateTo }
    })
    return response.data
  },
  getBalance: async (params = {}) => {
    const response = await api.get('/api/financial/cash-flow/balance', { params })
    return response.data
  }
}

// ============================================
// MÓDULO FINANCEIRO - SERVIÇOS ADICIONAIS
// ============================================
export const financialCategoryService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/financial/categories', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/financial/categories/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/financial/categories', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/financial/categories/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/financial/categories/${id}`)
    return response.data
  }
}

export const costCenterService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/financial/cost-centers', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/financial/cost-centers/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/financial/cost-centers', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/financial/cost-centers/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/financial/cost-centers/${id}`)
    return response.data
  }
}

export const financialAccountService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/financial/accounts', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/financial/accounts/${id}`)
    return response.data
  },
  getByCode: async (code) => {
    const response = await api.get(`/api/financial/accounts/code/${code}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/financial/accounts', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/financial/accounts/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/financial/accounts/${id}`)
    return response.data
  },
  updateBalance: async (id, amount, type) => {
    const response = await api.post(`/api/financial/accounts/${id}/update-balance`, {
      amount,
      type
    })
    return response.data
  },
  getBalance: async (id) => {
    const response = await api.get(`/api/financial/accounts/${id}/balance`)
    return response.data
  },
  getChildren: async (id) => {
    const response = await api.get(`/api/financial/accounts/${id}/children`)
    return response.data
  }
}

export const cardReconciliationService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/financial/card-reconciliations', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/financial/card-reconciliations/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/financial/card-reconciliations', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/financial/card-reconciliations/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/financial/card-reconciliations/${id}`)
    return response.data
  },
  process: async (id) => {
    const response = await api.post(`/api/financial/card-reconciliations/${id}/process`)
    return response.data
  }
}

// ============================================
// MÓDULO FINANCEIRO - CONCILIAÇÃO BANCÁRIA COM IA
// ============================================
export const bankReconciliationService = {
  // Upload de arquivo OFX
  uploadOfx: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post('/api/financial/bank-reconciliation/upload-ofx', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },
  
  // Análise de transações com Gemini
  analyze: async (transactions, financialAccountId = null, dateFrom = null, dateTo = null) => {
    const response = await api.post('/api/financial/bank-reconciliation/analyze', {
      transactions,
      financialAccountId,
      dateFrom,
      dateTo
    })
    return response.data
  },
  
  // Confirmar match sugerido
  confirmMatch: async (data) => {
    const response = await api.post('/api/financial/bank-reconciliation/confirm-match', data)
    return response.data
  },
  
  // Criar novo lançamento sugerido
  createSuggestedEntry: async (data) => {
    const response = await api.post('/api/financial/bank-reconciliation/create-suggested-entry', data)
    return response.data
  }
}

export const paymentReceiptMethodService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/financial/payment-receipt-methods', { params: filters })
    return response.data
  },
  // Endpoint acessível a todos os usuários com acesso ao ERP
  // Usado em PDV/Vendas onde usuários comuns precisam finalizar vendas
  getForSales: async (filters = {}) => {
    const response = await api.get('/api/financial/payment-receipt-methods/for-sales', { params: filters })
    return response.data
  },
  getHierarchy: async (filters = {}) => {
    const response = await api.get('/api/financial/payment-receipt-methods', { 
      params: { ...filters, hierarchy: true }
    })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/financial/payment-receipt-methods/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/financial/payment-receipt-methods', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/financial/payment-receipt-methods/${id}`, data)
    return response.data
  },
  delete: async (id, force = false) => {
    const response = await api.delete(`/api/financial/payment-receipt-methods/${id}`, {
      params: { force: force }
    })
    return response.data
  }
}

// ============================================
// MÓDULO FINANCEIRO - EXTRATO DE CAIXA (SINC. BANCÁRIA)
// ============================================
export const cashStatementService = {
  // Upload e importação de arquivo OFX
  uploadOfx: async (file, financialAccountId) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('financialAccountId', financialAccountId)
    
    const response = await api.post('/api/financial/cash-statement/import-ofx', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },
  
  // Buscar transações do extrato bancário
  getTransactions: async (filters = {}) => {
    const response = await api.get('/api/financial/cash-statement/transactions', { params: filters })
    return response.data
  },
  
  // Buscar saldo de uma conta financeira
  getBalance: async (financialAccountId) => {
    const response = await api.get(`/api/financial/cash-statement/balance/${financialAccountId}`)
    return response.data
  },
  
  // Validar integridade do saldo
  validateIntegrity: async (financialAccountId) => {
    const response = await api.get(`/api/financial/cash-statement/validate-integrity/${financialAccountId}`)
    return response.data
  },
  
  // Vincular transação do extrato com título do ERP
  linkTransaction: async (transactionId, erpTitleId, erpTitleType, settlementMode = 'partial', feeAmount = null) => {
    const body = {
      erpTitleId,
      erpTitleType,
      settlementMode
    }
    if (feeAmount != null && feeAmount !== '') {
      body.feeAmount = Number(feeAmount)
    }
    const response = await api.post(`/api/financial/cash-statement/transactions/${transactionId}/link`, body)
    return response.data
  },
  
  // Desvincular transação (todas as conciliações)
  unlinkTransaction: async (transactionId) => {
    const response = await api.delete(`/api/financial/cash-statement/transactions/${transactionId}/link`)
    return response.data
  },
  // Desvincular uma conciliação específica de uma transação
  unlinkReconciliation: async (transactionId, reconciliationId, erpTitleType = 'card_reconciliation') => {
    const response = await api.delete(`/api/financial/cash-statement/transactions/${transactionId}/reconciliations/${reconciliationId}?erpTitleType=${erpTitleType}`)
    return response.data
  },
  
  // Excluir transação do extrato
  deleteTransaction: async (transactionId) => {
    const response = await api.delete(`/api/financial/cash-statement/transactions/${transactionId}`)
    return response.data
  },

  // Criar transferência física-física, física-digital ou digital-física
  createTransfer: async (data) => {
    const response = await api.post('/api/financial/cash-statement/transfers', data)
    return response.data
  },

  // Buscar transferências pendentes para conciliação com OFX
  getPendingTransfers: async (filters = {}) => {
    const response = await api.get('/api/financial/cash-statement/transfers/pending', { params: filters })
    return response.data
  },

  // Conciliar crédito OFX com transferência pendente
  linkTransferBankTransaction: async (transferId, bankStatementTransactionId) => {
    const response = await api.post(`/api/financial/cash-statement/transfers/${transferId}/link-bank-transaction`, {
      bankStatementTransactionId
    })
    return response.data
  },

  // Vincular dois lançamentos OFX como transferência digital-digital
  linkDigitalTransfer: async (data) => {
    const response = await api.post('/api/financial/cash-statement/transfers/link-digital', data)
    return response.data
  },

  // Alterar conta destino de transferência física-digital pendente
  updateTransferDestination: async (transferId, destinationAccountId) => {
    const response = await api.patch(`/api/financial/cash-statement/transfers/${transferId}/destination`, {
      destinationAccountId
    })
    return response.data
  },

  // Corrigir valor de saída de transferência física-digital pendente
  updateTransferAmount: async (transferId, amount) => {
    const response = await api.patch(`/api/financial/cash-statement/transfers/${transferId}/amount`, {
      amount
    })
    return response.data
  }
}

// ============================================
// MÓDULO ORÇAMENTOS
// ============================================

// ============================================
// MÓDULO PROSPECÇÃO
// ============================================
export const leadService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/prospecting/leads', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/prospecting/leads/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/prospecting/leads', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/prospecting/leads/${id}`, data)
    return response.data
  },
  cancel: async (id) => {
    const response = await api.patch(`/api/prospecting/leads/${id}/cancel`)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/prospecting/leads/${id}`)
    return response.data
  },
  getInteractions: async (id) => {
    const response = await api.get(`/api/prospecting/leads/${id}/interactions`)
    return response.data
  },
  createInteraction: async (id, data) => {
    const response = await api.post(`/api/prospecting/leads/${id}/interactions`, data)
    return response.data
  },
  getAllCommunications: async (filters = {}) => {
    const response = await api.get('/api/prospecting/communications', { params: filters })
    return response.data
  },
  lookupCustomer: async (document) => {
    const response = await api.get('/api/prospecting/customers/lookup', { params: { document } })
    return response.data
  },
  wallets: async () => {
    const response = await api.get('/api/prospecting/wallets')
    return response.data
  },
  redistribute: async (leadIds, userIds) => {
    const response = await api.post('/api/prospecting/leads/redistribute', { leadIds, userIds })
    return response.data
  }
}

export const proposalService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/prospecting/proposals', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/prospecting/proposals/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/prospecting/proposals', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/prospecting/proposals/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/prospecting/proposals/${id}`)
    return response.data
  },
  send: async (id) => {
    const response = await api.post(`/api/prospecting/proposals/${id}/send`)
    return response.data
  },
  getByLeadId: async (leadId) => {
    const response = await api.get(`/api/prospecting/leads/${leadId}/proposals`)
    return response.data
  }
}

// ============================================
// MÓDULO FISCAL - NF-e MODELO 55
// ============================================
export const nfeService = {
  // Listar todas as NF-e modelo 55
  getAll: async (filters = {}) => {
    const response = await api.get('/api/v1/fiscal/nfe', { params: filters })
    return response.data
  },

  // Buscar NF-e por ID
  getById: async (id) => {
    const response = await api.get(`/api/v1/fiscal/nfe/${id}`)
    return response.data
  },

  // Pré-visualizar NF-e (sem emitir)
  preview: async (data) => {
    const response = await api.post('/api/v1/fiscal/nfe/preview', data)
    return response.data
  },

  // Emitir NF-e a partir de venda
  emitir: async (data) => {
    const response = await api.post('/api/v1/fiscal/nfe/emitir', data)
    return response.data
  },

  // Listar orçamentos aprovados disponíveis
  getAvailableQuotes: async () => {
    const response = await api.get('/api/v1/fiscal/nfe/quotes/available')
    return response.data
  },

  // Emitir NF-e a partir de orçamento
  emitirFromQuote: async (quoteId, data) => {
    const response = await api.post(`/api/v1/fiscal/nfe/from-quote/${quoteId}`, data)
    return response.data
  },

  // Consultar status de uma NF-e na SEFAZ
  consultarStatus: async (id) => {
    const response = await api.post(`/api/v1/fiscal/nfe/${id}/consultar-status`)
    return response.data
  },

  // Corrige a razão social do cliente vinculado à NF-e
  corrigirRazaoSocial: async (id, name) => {
    const response = await api.post(`/api/v1/fiscal/nfe/${id}/corrigir-razao-social`, { name })
    return response.data
  },

  // Cancelar uma NF-e autorizada
  cancelar: async (id, justificativa) => {
    const response = await api.post(`/api/v1/fiscal/nfe/${id}/cancelar`, { justificativa })
    return response.data
  },

  // Emitir Carta de Correção Eletrônica (CCe)
  emitirCartaCorrecao: async (id, correcao) => {
    const response = await api.post(`/api/v1/fiscal/nfe/${id}/carta-correcao`, { correcao })
    return response.data
  },

  // Criar rascunho de NF-e
  criarRascunho: async (data) => {
    const response = await api.post('/api/v1/fiscal/nfe/rascunho', data)
    return response.data
  },

  // Salvar rascunho de NF-e
  salvarRascunho: async (id, data) => {
    const response = await api.put(`/api/v1/fiscal/nfe/${id}/salvar`, data)
    return response.data
  },

  // Verificar estoque antes de finalizar
  verificarEstoque: async (id) => {
    const response = await api.get(`/api/v1/fiscal/nfe/${id}/verificar-estoque`)
    return response.data
  },

  // Iniciar finalização (atualiza status para 'finalizing')
  iniciarFinalizacao: async (id) => {
    const response = await api.post(`/api/v1/fiscal/nfe/${id}/iniciar-finalizacao`)
    return response.data
  },

  // Finalizar venda e emitir NF-e
  finalizarVenda: async (id, data) => {
    console.log('🌐 API: Chamando endpoint finalizarVenda');
    console.log('🌐 URL:', `/api/v1/fiscal/nfe/${id}/finalizar`);
    console.log('🌐 Método: POST');
    console.log('🌐 Dados:', data);
    
    try {
      const response = await api.post(`/api/v1/fiscal/nfe/${id}/finalizar`, data)
      console.log('🌐 API: Resposta recebida:', {
        status: response.status,
        success: response.data?.success,
        message: response.data?.message
      });
      return response.data
    } catch (error) {
      console.error('🌐 API: Erro na requisição:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // Transmitir NF-e via FocusNFe
  transmitirNota: async (id) => {
    console.log('🌐 API: Chamando endpoint transmitirNota');
    console.log('🌐 URL:', `/api/v1/fiscal/nfe/${id}/transmitir`);
    console.log('🌐 Método: POST');
    
    try {
      const response = await api.post(`/api/v1/fiscal/nfe/${id}/transmitir`)
      console.log('🌐 API: Resposta recebida:', {
        status: response.status,
        success: response.data?.success,
        message: response.data?.message
      });
      return response.data
    } catch (error) {
      console.error('🌐 API: Erro na requisição:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // Adicionar item ao rascunho
  adicionarItem: async (id, item) => {
    const response = await api.post(`/api/v1/fiscal/nfe/${id}/itens`, item)
    return response.data
  },

  // Atualizar item do rascunho
  atualizarItem: async (id, itemId, item) => {
    const response = await api.put(`/api/v1/fiscal/nfe/${id}/itens/${itemId}`, item)
    return response.data
  },

  // Remover item do rascunho
  removerItem: async (id, itemId) => {
    const response = await api.delete(`/api/v1/fiscal/nfe/${id}/itens/${itemId}`)
    return response.data
  },

  // Baixar PDF DANFE
  downloadPDF: async (id) => {
    const response = await api.get(`/api/v1/fiscal/nfe/${id}/pdf`, {
      responseType: 'blob'
    })
    return response.data
  },


  // Visualizar DANFE (retorna URL para exibição no modal)
  visualizarDANFE: async (id) => {
    try {
      // Fazer requisição autenticada para obter URL do PDF
      // Garantir que o parâmetro format=json seja enviado corretamente na URL
      // Usar URL completa para garantir que o parâmetro seja enviado
      const url = `/api/v1/fiscal/nfe/${id}/pdf?format=json`
      
      console.log('🔍 [visualizarDANFE] Fazendo requisição para:', url)
      
      const response = await api.get(url, {
        // Garantir que a resposta seja tratada como JSON
        responseType: 'json',
        // Não seguir redirects automaticamente
        validateStatus: (status) => {
          // Aceitar apenas status 2xx como sucesso
          // Rejeitar 3xx (redirects) para evitar redirecionamentos inesperados
          return status >= 200 && status < 300
        }
      })
      
      console.log('✅ [visualizarDANFE] Resposta recebida:', {
        status: response.status,
        data: response.data
      })
      
      // Verificar se a resposta é válida
      if (!response || !response.data) {
        throw new Error('Resposta inválida do servidor')
      }
      
      // Verificar se o backend retornou um redirect (status 3xx)
      if (response.status >= 300 && response.status < 400) {
        throw new Error('O servidor retornou um redirecionamento. Verifique se o parâmetro format=json está sendo enviado corretamente.')
      }
      
      // Verificar se a resposta tem o formato esperado
      if (response.data && response.data.success && response.data.data?.url) {
        const pdfUrl = response.data.data.url
        
        // Validar se a URL é válida antes de abrir
        if (!pdfUrl || typeof pdfUrl !== 'string' || pdfUrl.trim() === '') {
          throw new Error('URL do PDF inválida ou vazia')
        }
        
        // Verificar se a URL parece ser uma URL válida do DANFE
        // URLs da FocusNFe geralmente contêm 'danfe' ou 'focusnfe' no caminho
        const urlLower = pdfUrl.toLowerCase()
        const isFocusNFeUrl = urlLower.includes('focusnfe') || urlLower.includes('danfe')
        
        console.log('📄 [visualizarDANFE] URL do DANFE:', {
          url: pdfUrl,
          isFocusNFeUrl: isFocusNFeUrl,
          length: pdfUrl.length
        })
        
        // Verificar se é uma URL absoluta válida
        try {
          const urlObj = new URL(pdfUrl)
          
          // Validar que é uma URL HTTP/HTTPS
          if (!['http:', 'https:'].includes(urlObj.protocol)) {
            throw new Error(`Protocolo inválido: ${urlObj.protocol}. Esperado http: ou https:`)
          }
          
          // Retornar URL para uso no modal
          return { 
            success: true,
            url: pdfUrl
          }
        } catch (urlError) {
          // Se não for uma URL absoluta, pode ser relativa
          // Tentar construir URL completa
          if (pdfUrl.startsWith('/')) {
            // URL relativa ao domínio - construir URL completa
            const baseUrl = window.location.origin
            const fullUrl = `${baseUrl}${pdfUrl}`
            
            console.log('🔗 [visualizarDANFE] Construindo URL completa:', fullUrl)
            
            // Retornar URL completa para uso no modal
            return { success: true, url: fullUrl }
          } else {
            throw new Error(`URL do PDF não é válida: ${pdfUrl}. Erro: ${urlError.message}`)
          }
        }
      } else {
        // Se não tem a estrutura esperada, verificar se é um erro
        const errorMessage = response.data?.message || 
                           response.data?.error || 
                           'URL do PDF não disponível na resposta do servidor'
        throw new Error(errorMessage)
      }
    } catch (error) {
      console.error('Erro ao obter URL do DANFE:', error)
      
      // Não permitir que erros causem redirecionamentos
      // Apenas lançar o erro para ser tratado pelo componente
      if (error.response) {
        // Erro da API
        // Verificar se é um redirect (status 3xx)
        if (error.response.status >= 300 && error.response.status < 400) {
          throw new Error('O servidor tentou redirecionar. Verifique se o parâmetro format=json está sendo enviado corretamente.')
        }
        
        const errorMessage = error.response.data?.message || 
                           error.response.data?.error || 
                           `Erro ao visualizar DANFE (${error.response.status}). A NF-e pode ainda estar sendo processada.`
        throw new Error(errorMessage)
      } else if (error.message) {
        // Erro lançado manualmente
        throw error
      } else {
        throw new Error('Erro desconhecido ao visualizar DANFE')
      }
    }
  }
}

export const quoteService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/quotes', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/quotes/${id}`)
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/quotes', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/api/quotes/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/api/quotes/${id}`)
    return response.data
  },
  open: async (id) => {
    const response = await api.post(`/api/quotes/${id}/open`)
    return response.data
  },
  approve: async (id) => {
    const response = await api.post(`/api/quotes/${id}/approve`)
    return response.data
  },
  revertToDraft: async (id) => {
    const response = await api.post(`/api/quotes/${id}/revert-to-draft`)
    return response.data
  },
  cancel: async (id) => {
    const response = await api.post(`/api/quotes/${id}/cancel`)
    return response.data
  },
  getApproved: async (search = '') => {
    const response = await api.get('/api/quotes/approved/list', { params: { search } })
    return response.data
  },
  validateReservation: async (id) => {
    const response = await api.get(`/api/quotes/${id}/validate-reservation`)
    return response.data
  },
  requestReservation: async (id) => {
    const response = await api.post(`/api/quotes/${id}/request-reservation`)
    return response.data
  },
  getDashboardStats: async (filters = {}) => {
    const response = await api.get('/api/quotes/dashboard/stats', { params: filters })
    return response.data
  },
  preview: async (id) => {
    const response = await api.get(`/api/quotes/${id}/preview`)
    return response.data
  },
  generatePDF: async (id) => {
    const response = await api.get(`/api/quotes/${id}/pdf`, { responseType: 'blob' })
    return response.data
  },
  // Itens
  addItem: async (quoteId, itemData) => {
    const response = await api.post(`/api/quotes/${quoteId}/items`, itemData)
    return response.data
  },
  updateItem: async (quoteId, itemId, itemData) => {
    const response = await api.put(`/api/quotes/${quoteId}/items/${itemId}`, itemData)
    return response.data
  },
  deleteItem: async (quoteId, itemId) => {
    const response = await api.delete(`/api/quotes/${quoteId}/items/${itemId}`)
    return response.data
  }
}

// ============================================
// MÓDULO CONFIGURAÇÕES
// ============================================
export const digitalCertificateService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/settings/certificates', { params: filters })
    return response.data
  },
  getActive: async () => {
    const response = await api.get('/api/settings/certificates/active')
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/settings/certificates', data)
    return response.data
  },
  validate: async (id) => {
    const response = await api.get(`/api/settings/certificates/${id}/validate`)
    return response.data
  },
  upload: async (file, password, name) => {
    const formData = new FormData()
    formData.append('certificate', file)
    formData.append('password', password)
    if (name) formData.append('name', name)
    const response = await api.post('/api/settings/certificates/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  }
}

// ============================================
// MÓDULO TABELAS ESPECIAIS DE PREÇOS
// ============================================
export const specialPricingTablesService = {
  // Listar todas as tabelas
  getAll: async (filters = {}) => {
    const response = await api.get('/api/sales/special-tables', { params: filters })
    return response.data
  },

  // Buscar tabela por ID
  getById: async (id) => {
    const response = await api.get(`/api/sales/special-tables/${id}`)
    return response.data
  },

  // Buscar tabelas de um cliente
  getByClientId: async (clientId) => {
    const response = await api.get(`/api/sales/special-tables/client/${clientId}`)
    return response.data
  },

  // Criar nova tabela
  create: async (data) => {
    const response = await api.post('/api/sales/special-tables', data)
    return response.data
  },

  // Atualizar tabela
  update: async (id, data) => {
    const response = await api.put(`/api/sales/special-tables/${id}`, data)
    return response.data
  },

  // Remover tabela
  delete: async (id) => {
    const response = await api.delete(`/api/sales/special-tables/${id}`)
    return response.data
  },

  // Listar itens de uma tabela
  getItems: async (tableId, filters = {}) => {
    const response = await api.get(`/api/sales/special-tables/${tableId}/items`, { params: filters })
    return response.data
  },

  // Adicionar produto à tabela
  addItem: async (tableId, data) => {
    const response = await api.post(`/api/sales/special-tables/${tableId}/items`, data)
    return response.data
  },

  // Atualizar preço de produto na tabela
  updateItem: async (tableId, productId, data) => {
    const response = await api.put(`/api/sales/special-tables/${tableId}/items/${productId}`, data)
    return response.data
  },

  // Remover produto da tabela
  removeItem: async (tableId, productId) => {
    const response = await api.delete(`/api/sales/special-tables/${tableId}/items/${productId}`)
    return response.data
  },

  // Calcular preço de produto para um cliente
  // tableId é opcional - se fornecido, usa essa tabela específica
  calculatePrice: async (clientId, productId, tableId = null) => {
    let url = `/api/sales/special-tables/calculate-price/client/${clientId}/product/${productId}`
    if (tableId) {
      url += `?tableId=${tableId}`
    }
    const response = await api.get(url)
    return response.data
  }
}

// ============================================
// MÓDULO MENSAGENS
// ============================================
export const boletoService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/financial/boletos', { params: filters })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/financial/boletos/${id}`)
    return response.data
  },
  create: async (accountReceivableId, data = {}) => {
    const response = await api.post('/api/financial/boletos', {
      accountReceivableId,
      ...data
    })
    return response.data
  },
  syncStatus: async (id) => {
    const response = await api.post(`/api/financial/boletos/${id}/sync`)
    return response.data
  },
  updateStatus: async (id, status) => {
    const response = await api.patch(`/api/financial/boletos/${id}/status`, { status })
    return response.data
  },
  getStats: async (dateFrom = null, dateTo = null) => {
    const params = {}
    if (dateFrom) params.dateFrom = dateFrom
    if (dateTo) params.dateTo = dateTo
    const response = await api.get('/api/financial/boletos/dashboard/stats', { params })
    return response.data
  },
  downloadPdf: async (id) => {
    try {
      const boleto = await boletoService.getById(id)
      console.log('Boleto retornado:', boleto)
      if (boleto.success && boleto.data) {
        // Tentar diferentes nomes de campos (snake_case e camelCase)
        const pdfUrl = boleto.data.pdf_url || boleto.data.pdfUrl
        console.log('PDF URL encontrada:', pdfUrl)
        if (pdfUrl && pdfUrl.trim() !== '') {
          window.open(pdfUrl, '_blank')
          return { success: true }
        }
        console.error('PDF URL não encontrada ou vazia. Campos disponíveis:', Object.keys(boleto.data))
        console.error('Valor de pdf_url:', boleto.data.pdf_url)
        console.error('Valor de pdfUrl:', boleto.data.pdfUrl)
      }
      return { success: false, message: 'URL do PDF não disponível' }
    } catch (error) {
      console.error('Erro ao baixar PDF:', error)
      return { success: false, message: error.message || 'Erro ao baixar PDF' }
    }
  },
  downloadPng: async (id) => {
    const boleto = await boletoService.getById(id)
    if (boleto.success && boleto.data.png_url) {
      window.open(boleto.data.png_url, '_blank')
      return { success: true }
    }
    return { success: false, message: 'URL da imagem não disponível' }
  }
}

export const messageService = {
  // Listar conversas do usuário
  getConversations: async (filters = {}) => {
    const response = await api.get('/api/messages/conversations', { params: filters })
    return response.data
  },

  // Buscar conversa por ID
  getConversation: async (id) => {
    const response = await api.get(`/api/messages/conversations/${id}`)
    return response.data
  },

  // Criar nova conversa
  createConversation: async (data) => {
    const response = await api.post('/api/messages/conversations', data)
    return response.data
  },

  // Marcar conversa como lida
  markConversationAsRead: async (id) => {
    const response = await api.put(`/api/messages/conversations/${id}/read`)
    return response.data
  },

  // Deletar conversa
  deleteConversation: async (id) => {
    const response = await api.delete(`/api/messages/conversations/${id}`)
    return response.data
  },

  // Listar mensagens de uma conversa
  getMessages: async (conversationId, filters = {}) => {
    const response = await api.get(`/api/messages/conversations/${conversationId}/messages`, { params: filters })
    return response.data
  },

  // Enviar mensagem
  sendMessage: async (conversationId, content) => {
    const response = await api.post(`/api/messages/conversations/${conversationId}/messages`, { content })
    return response.data
  },

  // Deletar mensagem
  deleteMessage: async (id) => {
    const response = await api.delete(`/api/messages/messages/${id}`)
    return response.data
  },

  // Listar usuários disponíveis para mensagens (sem precisar de permissão PEOPLE_USERS)
  getAvailableUsers: async () => {
    const response = await api.get('/api/messages/users')
    return response.data
  },

  // Contar mensagens não lidas
  getUnreadCount: async () => {
    const response = await api.get('/api/messages/notifications/unread-count')
    return response.data
  }
}

export const operationalAssistantService = {
  getHealth: async () => {
    const response = await api.get('/api/operational-assistant/health')
    return response.data
  },

  getConversations: async () => {
    const response = await api.get('/api/operational-assistant/conversations')
    return response.data
  },

  getConversation: async (id) => {
    const response = await api.get(`/api/operational-assistant/conversations/${id}`)
    return response.data
  },

  createConversation: async (title) => {
    const response = await api.post('/api/operational-assistant/conversations', { title })
    return response.data
  },

  deleteConversation: async (id) => {
    const response = await api.delete(`/api/operational-assistant/conversations/${id}`)
    return response.data
  },

  sendMessage: async (conversationId, message) => {
    const response = await api.post(`/api/operational-assistant/conversations/${conversationId}/messages`, { message })
    return response.data
  },

  chat: async (message) => {
    const response = await api.post('/api/operational-assistant/chat', { message })
    return response.data
  }
}

export const sellerAssistantService = {
  getHealth: async () => {
    const response = await api.get('/api/seller-assistant/health')
    return response.data
  },

  getConversations: async () => {
    const response = await api.get('/api/seller-assistant/conversations')
    return response.data
  },

  getConversation: async (id) => {
    const response = await api.get(`/api/seller-assistant/conversations/${id}`)
    return response.data
  },

  createConversation: async (title) => {
    const response = await api.post('/api/seller-assistant/conversations', { title })
    return response.data
  },

  deleteConversation: async (id) => {
    const response = await api.delete(`/api/seller-assistant/conversations/${id}`)
    return response.data
  },

  sendMessage: async (conversationId, message) => {
    const response = await api.post(`/api/seller-assistant/conversations/${conversationId}/messages`, { message })
    return response.data
  },

  chat: async (message) => {
    const response = await api.post('/api/seller-assistant/chat', { message })
    return response.data
  }
}

const erpApiDefault = {
  customerService,
  supplierService,
  accessGroupService,
  productGroupService,
  brandService,
  unitService,
  productService,
  productGridService,
  productVariantService,
  variantTypeService,
  stockMovementService,
  stockMovementTypeService,
  invoiceEntryService,
  returnService,
  customerCreditService,
  locationService,
  stockAuditService,
  saleService,
  saleReturnService,
  salePaymentService,
  paymentMethodService,
  paymentTermService,
  invoiceService,
  cashRegisterService,
  accountReceivableService,
  accountPayableService,
  cashFlowService,
  financialCategoryService,
  costCenterService,
  cardReconciliationService,
  bankReconciliationService,
  paymentReceiptMethodService,
  cashStatementService,
  digitalCertificateService,
  quoteService,
  nfeService,
  specialPricingTablesService,
  boletoService,
  messageService,
  sellerAssistantService,
  operationalAssistantService
}

export default erpApiDefault
